import { z } from 'zod';

/**
 * How a team pays for AI tokens (canon §B.2):
 *   - `byok`    — the team's own provider key (default, zero financial risk to us)
 *   - `managed` — our key, billed/credited with cost caps (operator opt-in)
 */
export const AiModeSchema = z.enum(['byok', 'managed']);
export type AiMode = z.infer<typeof AiModeSchema>;

/**
 * Team AI-mode status — what the team chose, what AI calls actually use right
 * now, and whether Managed is even selectable for this team.
 */
export const AiModeStatusSchema = z.object({
  /** The team's stored choice. */
  mode: AiModeSchema,
  /** What AI calls resolve to right now (managed only when globally enabled +
   *  the team is AI-entitled; otherwise falls back to byok). */
  effectiveMode: AiModeSchema,
  /** Can this team get Managed at all? (global FEATURE_AI_MANAGED on + AI-entitled) */
  managedAvailable: z.boolean(),
  /**
   * Whether the team has the paid Managed-AI add-on active (canon §B.2.1).
   * `managedAvailable && !managedPurchased` → show the upsell; both true →
   * Managed is selectable.  Until Stripe self-serve lands, this is granted by
   * an operator (the `managed_ai` feature flag).
   */
  managedPurchased: z.boolean(),
  /** Whether the team's plan includes AI at all. */
  aiEntitled: z.boolean(),
});
export type AiModeStatusProjection = z.infer<typeof AiModeStatusSchema>;

export const SetAiModeSchema = z.object({ mode: AiModeSchema });
export type SetAiModeInput = z.infer<typeof SetAiModeSchema>;
